import type { ProjectsApi } from './api';
import { ConflictError, SyncModeError } from './api';
import { projectStore } from './projectStore';
import { decideReconcile } from './reconcile';
import { useCurrentProject } from './currentProject';
import { captureCanvasCover } from './captureCover';
import { captureLargestFrameJpeg } from '../render/captureFrameDom';

export interface SyncDeps {
  api: Pick<ProjectsApi, 'get' | 'saveDoc' | 'putCover'>;
  /** Apply a serialized CanvasDoc into the canvas store + render. */
  loadDoc: (docJson: string) => void;
}

export function makeSyncController(deps: SyncDeps) {
  async function adopt(projectId: string, docJson: string, version: number, title: string): Promise<void> {
    deps.loadDoc(docJson);
    await projectStore.save({ projectId, doc: docJson, baseVersion: version, dirty: false, updatedAt: Date.now() });
    useCurrentProject.getState().setCurrent(projectId, version, title);
  }

  return {
    async openProject(projectId: string): Promise<void> {
      const local = await projectStore.load(projectId);
      if (local) deps.loadDoc(local.doc); // instant render from cache

      const server = await deps.api.get(projectId);
      useCurrentProject.getState().setRole(server.role);
      const decision = local
        ? decideReconcile({ localDirty: local.dirty, localBaseVersion: local.baseVersion }, { docVersion: server.docVersion })
        : { action: 'adopt-server' as const };

      if (decision.action === 'adopt-server') { await adopt(projectId, server.doc, server.docVersion, server.title); return; }
      if (decision.action === 'keep-local') {
        deps.loadDoc(local!.doc);
        useCurrentProject.getState().setCurrent(projectId, local!.baseVersion, server.title);
        return;
      }
      // push: send the dirty local doc; 409 → keep-mine (re-push onto server version).
      try {
        const r = await deps.api.saveDoc(projectId, local!.doc, local!.baseVersion);
        await projectStore.save({ projectId, doc: local!.doc, baseVersion: r.docVersion, dirty: false, updatedAt: Date.now() });
        useCurrentProject.getState().setCurrent(projectId, r.docVersion, server.title);
      } catch (e) {
        if (e instanceof SyncModeError) {
          // Project is live-sync-owned but this client opted out of sync. The
          // dirty local copy stays cached; the banner asks for a reload into
          // live sync. No REST retry — it would be rejected forever.
          useCurrentProject.getState().setCurrent(projectId, local!.baseVersion, server.title);
          useCurrentProject.getState().setSyncUpgradeRequired(true);
        } else if (e instanceof ConflictError) {
          const r = await deps.api.saveDoc(projectId, local!.doc, e.docVersion); // keep-mine
          await projectStore.save({ projectId, doc: local!.doc, baseVersion: r.docVersion, dirty: false, updatedAt: Date.now() });
          useCurrentProject.getState().setCurrent(projectId, r.docVersion, server.title);
        } else { throw e; }
      }
    },

    /** Persist the current doc to local cache (dirty) + push to the server. */
    async saveCurrent(projectId: string, docJson: string): Promise<void> {
      const { baseVersion, title, syncUpgradeRequired } = useCurrentProject.getState();
      // Cache locally first — data is safe even when the server refuses REST.
      await projectStore.save({ projectId, doc: docJson, baseVersion, dirty: true, updatedAt: Date.now() });
      if (syncUpgradeRequired) return; // REST is permanently rejected; banner is up
      try {
        const r = await deps.api.saveDoc(projectId, docJson, baseVersion);
        await projectStore.save({ projectId, doc: docJson, baseVersion: r.docVersion, dirty: false, updatedAt: Date.now() });
        useCurrentProject.getState().setCurrent(projectId, r.docVersion, title ?? '');
      } catch (e) {
        if (e instanceof SyncModeError) { useCurrentProject.getState().setSyncUpgradeRequired(true); return; }
        if (!(e instanceof ConflictError)) throw e; // offline/transient → stays dirty, retried next save
      }
    },

    /** Snapshot + upload the outgoing project's cover (fire-and-forget).
     *  Canvas bitmap when present, else a DOM capture of the largest frame —
     *  DOM-mode projects no longer fall back to the gradient placeholder. */
    async captureCover(projectId: string): Promise<void> {
      const dataUrl = captureCanvasCover() ?? (await captureLargestFrameJpeg());
      if (!dataUrl) return;
      try { await deps.api.putCover(projectId, dataUrl); }
      catch (e) { console.warn('[codeframe] cover upload failed', e); }
    },
  };
}
